import React, { useState, useMemo } from 'react'
import { useDataProvider } from 'react-admin'
import { Field } from 'react-final-form'
import {
  Box,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Button,
  Typography,
  InputAdornment,
  makeStyles,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd'
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'stretch',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  pane: {
    flex: '1 1 280px',
    minWidth: 280,
    maxWidth: 400,
    maxHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 200,
    overflow: 'hidden',
  },
  paneHeader: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  searchField: {
    margin: theme.spacing(1, 2),
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  list: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
    overflowY: 'auto',
  },
  listItem: {
    cursor: 'pointer',
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
  addAllButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
}))

const filterBySearch = (items, search, getLabel) => {
  if (!search || !search.trim()) return items
  const q = search.trim().toLowerCase()
  return items.filter((item) => (getLabel(item) || '').toLowerCase().includes(q))
}

/**
 * Presentational dual listbox. No form hooks, no useEffect – receives data and callbacks as props
 * so it cannot cause update loops.
 */
const ModalityDualListBoxUI = ({
  label = 'Modalities',
  modalities = [],
  loading = false,
  value = [],
  onChange,
}) => {
  const classes = useStyles()
  const [leftSearch, setLeftSearch] = useState('')
  const [rightSearch, setRightSearch] = useState('')
  const [checkedAvailable, setCheckedAvailable] = useState([])
  const [checkedSelected, setCheckedSelected] = useState([])

  const selectedIds = Array.isArray(value) ? value : []

  const available = useMemo(
    () => modalities.filter((m) => !selectedIds.includes(m.id)),
    [modalities, selectedIds]
  )
  const selected = useMemo(() => {
    const order = new Map(selectedIds.map((id, i) => [id, i]))
    return modalities
      .filter((m) => selectedIds.includes(m.id))
      .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0))
  }, [modalities, selectedIds])

  const availableFiltered = useMemo(
    () => filterBySearch(available, leftSearch, (m) => m.displayName || m.serialNumber || m.id),
    [available, leftSearch]
  )
  const selectedFiltered = useMemo(
    () => filterBySearch(selected, rightSearch, (m) => m.displayName || m.serialNumber || m.id),
    [selected, rightSearch]
  )

  const handleAddAll = () => {
    if (onChange) onChange(modalities.map((m) => m.id))
    setCheckedAvailable([])
  }

  const handleAddSelected = () => {
    if (checkedAvailable.length === 0 || !onChange) return
    const added = checkedAvailable.map((m) => m.id)
    onChange([...selectedIds, ...added])
    setCheckedAvailable([])
  }

  const handleRemoveSelected = () => {
    if (checkedSelected.length === 0 || !onChange) return
    const toRemove = new Set(checkedSelected.map((m) => m.id))
    onChange(selectedIds.filter((id) => !toRemove.has(id)))
    setCheckedSelected([])
  }

  const handleRemoveAll = () => {
    if (onChange) onChange([])
    setCheckedSelected([])
  }

  const toggleAvailable = (modality) => {
    setCheckedAvailable((prev) =>
      prev.some((m) => m.id === modality.id)
        ? prev.filter((m) => m.id !== modality.id)
        : [...prev, modality]
    )
  }

  const toggleSelected = (modality) => {
    setCheckedSelected((prev) =>
      prev.some((m) => m.id === modality.id)
        ? prev.filter((m) => m.id !== modality.id)
        : [...prev, modality]
    )
  }

  const isAvailableChecked = (modality) => checkedAvailable.some((m) => m.id === modality.id)
  const isSelectedChecked = (modality) => checkedSelected.some((m) => m.id === modality.id)

  const getLabel = (m) => m.displayName || m.serialNumber || `Modality ${m.id}`

  if (loading) {
    return (
      <Box py={2}>
        <Typography color="textSecondary">Loading modalities…</Typography>
      </Box>
    )
  }

  return (
    <Box width="100%">
      {label && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          {label}
        </Typography>
      )}
      <Box className={classes.root}>
        <Paper variant="outlined" className={classes.pane}>
          <div className={classes.paneHeader}>
            <Typography variant="subtitle2">Available</Typography>
            <Typography variant="caption" color="textSecondary">
              {available.length} of {modalities.length}
            </Typography>
          </div>
          <TextField
            size="small"
            placeholder="Search…"
            value={leftSearch}
            onChange={(e) => setLeftSearch(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <List dense className={classes.list}>
            {availableFiltered.map((m) => (
              <ListItem
                key={m.id}
                button
                className={classes.listItem}
                onClick={() => toggleAvailable(m)}
              >
                <Checkbox
                  edge="start"
                  checked={isAvailableChecked(m)}
                  tabIndex={-1}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleAvailable(m)}
                />
                <ListItemText primary={getLabel(m)} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
            {availableFiltered.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={available.length === 0 ? 'All modalities selected' : 'No matches'}
                  primaryTypographyProps={{ variant: 'body2', color: 'textSecondary' }}
                />
              </ListItem>
            )}
          </List>
        </Paper>

        <div className={classes.buttonGroup}>
          <Button
            variant="outlined"
            size="small"
            className={classes.addAllButton}
            onClick={handleAddAll}
            startIcon={<PlaylistAddIcon />}
            title="Add all modalities"
          >
            Add all
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleAddSelected}
            disabled={checkedAvailable.length === 0}
            endIcon={<ChevronRightIcon />}
          >
            Add
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRemoveSelected}
            disabled={checkedSelected.length === 0}
            startIcon={<ChevronLeftIcon />}
          >
            Remove
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRemoveAll}
            disabled={selected.length === 0}
            startIcon={<RemoveCircleOutlineIcon />}
            title="Remove all"
          >
            Remove all
          </Button>
        </div>

        <Paper variant="outlined" className={classes.pane}>
          <div className={classes.paneHeader}>
            <Typography variant="subtitle2">Included in alert</Typography>
            <Typography variant="caption" color="textSecondary">
              {selected.length}
            </Typography>
          </div>
          <TextField
            size="small"
            placeholder="Search…"
            value={rightSearch}
            onChange={(e) => setRightSearch(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <List dense className={classes.list}>
            {selectedFiltered.map((m) => (
              <ListItem
                key={m.id}
                button
                className={classes.listItem}
                onClick={() => toggleSelected(m)}
              >
                <Checkbox
                  edge="start"
                  checked={isSelectedChecked(m)}
                  tabIndex={-1}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelected(m)}
                />
                <ListItemText primary={getLabel(m)} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
            {selectedFiltered.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={selected.length === 0 ? 'No modalities selected' : 'No matches'}
                  primaryTypographyProps={{ variant: 'body2', color: 'textSecondary' }}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </Box>
    </Box>
  )
}

/**
 * Form field wrapper: fetches modalities once and binds to form via FormDataConsumer.
 * Fetch runs in a dedicated component so the presentational UI never has useEffect/useInput.
 */
const ModalityDualListBoxField = ({ label = 'Modalities', source = 'modalityIds' }) => {
  const dataProvider = useDataProvider()
  const [modalities, setModalities] = useState([])
  const [loading, setLoading] = useState(true)
  const fetchedRef = React.useRef(false)

  React.useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    let cancelled = false
    dataProvider
      .getList('modalities', {
        pagination: { page: 1, perPage: 5000 },
        sort: { field: 'displayName', order: 'ASC' },
        filter: {},
      })
      .then(({ data }) => {
        if (!cancelled) {
          setModalities(Array.isArray(data) ? data : [])
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setModalities([])
          setLoading(false)
        }
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
  }, [])

  return (
    <Field name={source} subscription={{ value: true }}>
      {({ input }) => (
        <ModalityDualListBoxUI
          label={label}
          modalities={modalities}
          loading={loading}
          value={Array.isArray(input.value) ? input.value : []}
          onChange={(ids) => input.onChange(ids)}
        />
      )}
    </Field>
  )
}

export default ModalityDualListBoxField
export { ModalityDualListBoxUI }
